import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios from 'axios';
import CancelActionButton from 'components/CancelActionButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import { DropdownOption } from 'components/final-form/dropdownUtils';
import Select from 'components/final-form/Select';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import SaveActionButton from 'components/SaveActionButton';
import { FormApi } from 'final-form';
import React, { useEffect, useState } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Table } from 'semantic-ui-react';
import { getAllSpecialCaseProducts, saveSpecialCaseProduct } from 'services/productServices';
import styled from 'styled-components';
import { SpecialCase } from 'ts-types/api.enums';
import { ProductDto, SpecialCaseProductRequestDto } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';

const TableContainer = styled.div`

  .dropdown.error-mandatory-field {
    border-color: #e0b4b4;
    background-color: #fff6f6;
  }
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  onCancel: () => void;
  onSave: () => void;
  foundProducts: ProductDto[];
}

const SpecialCaseProductModal = (props: Props) => {

  const {foundProducts, language, onCancel, getMessage} = props;

  const cancelTokenSource = axios.CancelToken.source();

  const productDropdownOptions = foundProducts.map(p => {
    const type = getMessage(`enum.InterventionExternalCode.${p.externalCode}`)

    const descMap: { [lang: string]: string } = {
      "de": p.description,
      "en": p.descriptionEn || p.description,
      "fr": p.descriptionFr || p.description,
      "it": p.descriptionIt || p.description
    };

    return {
      key: p.id,
      value: p.id,
      text: `${type}, ${p.id}, ${descMap[language]}`
    }
  })

  const [specialCaseProducts, setSpecialCaseProducts] = useState({} as { [index: string]: string });
  const [specialCases] = useState(Object.values(SpecialCase) as SpecialCase[]);
  const [productOptions] = useState(productDropdownOptions as DropdownOption[]);
  const [editSpecialCase, setEditSpecialCase] = useState(undefined as SpecialCase | undefined);
  const [editProductId, setEditProductId] = useState(undefined as string | undefined);
  const [errorMessage, setErrorMessage] = useState(undefined as string | undefined);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const specialCaseProducts = await getAllSpecialCaseProducts(cancelTokenSource);
      setSpecialCaseProducts(specialCaseProducts);
    } catch (e) {
      handleError(e.response.data);
    }
  }

  const specialCaseProductDisplay = (product?: ProductDto) => {

    if (product) {
      const type = getMessage(`enum.InterventionExternalCode.${product.externalCode}`)

      const descMap: { [lang: string]: string } = {
        "de": product.description,
        "en": product.descriptionEn || product.description,
        "fr": product.descriptionFr || product.description,
        "it": product.descriptionIt || product.description
      };

      return `${type}, ${product.id}, ${descMap[language]}`;
    }

    return "";
  };

  const editSpecialCaseProduct = (specialCase: SpecialCase, productId: string | undefined) => {
    setEditSpecialCase(specialCase);
    setEditProductId(productId);
  };

  const clearSpecialCaseProduct = async (specialCase: SpecialCase, form: FormApi) => {
    try {
      const request: Partial<SpecialCaseProductRequestDto> = {
        specialCase,
        productId: undefined
      }

      await saveSpecialCaseProduct(request, cancelTokenSource);
    } catch (e) {
      handleError(e.response.data);
    } finally {
      setEditSpecialCase(undefined);
      setEditProductId(undefined);
    }
    form.change(specialCase, null);
  };

  const cancelEditSpecialCaseProduct = (specialCase: SpecialCase, form: FormApi) => {
    setEditSpecialCase(undefined);
    setEditProductId(undefined);
    form.change(specialCase, editProductId);
  };

  const updateSpecialCaseProduct = async (specialCase: SpecialCase, productId?: string) => {
    try {
      const request: Partial<SpecialCaseProductRequestDto> = {
        specialCase,
        productId
      }

      await saveSpecialCaseProduct(request, cancelTokenSource);

    } catch (e) {
      handleError(e.response.data);
    } finally {
      setEditSpecialCase(undefined);
      setEditProductId(undefined);
    }
  };

  const handleError = (error: any) => {
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [];

    const violations: Array<any> = error.violations;
    let knowErrorFound = false;
    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          knowErrorFound = true;
          setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    }

    if (!knowErrorFound) {
      if (knownErrors.includes(errorCode)) {
        setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        setErrorMessage(getMessage('error.general'));
      }
    }
  };

  const renderForm = ({handleSubmit, form}: FormRenderProps): React.ReactNode => {

    const values: { [index: string]: string | null | undefined } = form.getState().values;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage
                  && (
                      <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
                        {errorMessage}
                      </StyledErrorMessage>
                  )
              }
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <TableContainer>
                      <Table celled selectable size="small">
                        <Table.Header>
                          <Table.Row>
                            <Table.HeaderCell>{getMessage('product.specialCase')}</Table.HeaderCell>
                            <Table.HeaderCell>{getMessage('product.form.title')}</Table.HeaderCell>
                            <Table.HeaderCell textAlign={"center"}>{getMessage('actions')}</Table.HeaderCell>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {specialCases.map(specialCase => {
                            let specialCaseProduct = undefined;
                            if (values[specialCase]) {
                              specialCaseProduct = foundProducts.find(p => p.id === values[specialCase]);
                            }
                            const editing = editSpecialCase === specialCase;
                            return (
                                <Table.Row key={specialCase}>
                                  <Table.Cell width={4}>
                                    {getMessage(`enum.SpecialCaseProduct.${specialCase}`)}
                                  </Table.Cell>
                                  <Table.Cell width={10}>
                                    {
                                      editing
                                          ? <Field
                                              name={specialCase}
                                              component={Select}
                                              options={productOptions}
                                              search
                                              fluid
                                          />
                                          : specialCaseProductDisplay(specialCaseProduct)
                                    }
                                  </Table.Cell>
                                  <Table.Cell width={2} textAlign="center">
                                    {editing
                                        ? <React.Fragment>
                                          <SaveActionButton hoverMessage={getMessage('button.save')}
                                                            onConfirm={() => updateSpecialCaseProduct(
                                                                specialCase,
                                                                values[specialCase]!
                                                            )
                                                            }
                                                            disabled={false}
                                          />
                                          <CancelActionButton hoverMessage={getMessage('button.cancel')}
                                                              onConfirm={() =>
                                                                  cancelEditSpecialCaseProduct(specialCase, form)
                                                              }
                                          />
                                        </React.Fragment>
                                        : <React.Fragment>
                                          <EditActionButton
                                              hoverMessage={getMessage('msgEdit')}
                                              onConfirm={() =>
                                                  editSpecialCaseProduct(specialCase, values[specialCase]!)
                                              }
                                          />
                                          <DeleteActionButton
                                              hoverMessage={getMessage("msgDelete")}
                                              onConfirm={() => clearSpecialCaseProduct(specialCase, form)}
                                          />
                                        </React.Fragment>
                                    }

                                  </Table.Cell>
                                </Table.Row>
                            )
                          })
                          }
                        </Table.Body>
                      </Table>
                    </TableContainer>
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="button"
                      onClick={onCancel}>{getMessage("button.close")}
              </Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );

  };

  return (
      <FinalForm
          onSubmit={() => noop()}
          subscription={{pristine: true, submitting: true, values: true}}
          initialValues={specialCaseProducts}
          render={renderForm}
      />
  );

}

export default withMessages(withAuthContext(SpecialCaseProductModal));
